import { defineStore } from 'pinia'
import { ref, computed, watch, nextTick } from 'vue'
import { useVehiclesStore } from './vehicles' // To fetch vehicle details
import type { Vehicle } from '~/types'

// Interface for items stored in the cart - Removed quantity
export interface CartItem {
  id: string; // Use vehicle ID as the primary key
  dateAdded: Date;
  // Enriched data (optional, fetched when added)
  title?: string;
  price?: number;
  image?: string;
}

export const useCartStore = defineStore('cart', () => {
  // State
  const cartItems = ref<CartItem[]>([])
  const isCartOpen = ref(false)
  const isLoading = ref(false) // For loading state during add
  const error = ref<string | null>(null)

  // Getters
  // cartCount now just counts the number of items
  const cartCount = computed(() => {
    return cartItems.value.length;
  })

  // cartTotal sums the price of each unique item (quantity is always 1)
  const cartTotal = computed(() => {
    return cartItems.value.reduce((total, item) => {
      const price = item.price || 0
      // return total + (price * item.quantity) // Old logic
      return total + price; // New logic (qty = 1)
    }, 0)
  })

  const isInCart = (vehicleId: string): boolean => {
    return cartItems.value.some(item => item.id === vehicleId);
  }

  // Actions
  const openCart = () => {
    isCartOpen.value = true
  }

  const closeCart = () => {
    isCartOpen.value = false
  }

  const toggleCart = () => {
    isCartOpen.value = !isCartOpen.value
  }

  const addToCart = async (vehicleId: string) => {
    isLoading.value = true;
    error.value = null;
    try {
      const existingItemIndex = cartItems.value.findIndex(item => item.id === vehicleId);

      if (existingItemIndex > -1) {
        // Toggle the item in cart - if it exists, remove it
        cartItems.value.splice(existingItemIndex, 1);
        // Save the updated cart state
        saveClientState();
      } else {
        const vehiclesStore = useVehiclesStore();

        try {
          // First try to find the vehicle in the existing vehicles array
          let vehicleDetails = vehiclesStore.vehicles.find(v => v.id === vehicleId);

          // If not found in the store, try to fetch it from the API
          if (!vehicleDetails) {
            const fetchedVehicle = await vehiclesStore.fetchVehicleById(vehicleId);
            vehicleDetails = fetchedVehicle || undefined;
          }

          // Create a new cart item with the vehicle details or fallback values
          const newItem: CartItem = {
            id: vehicleId,
            dateAdded: new Date(),
            title: vehicleDetails?.title || 'Vehicle ' + vehicleId,
            price: Number(vehicleDetails?.price ?? 0),
            image: vehicleDetails?.thumb || vehicleDetails?.photos?.[0] || '/placeholder-car.jpg'
          };

          cartItems.value.push(newItem);

          // Save the updated cart state
          saveClientState();

          // Open cart when a NEW item is added
          openCart();
        } catch (fetchError) {
          // Add the item with minimal information even if we couldn't fetch details
          const fallbackItem: CartItem = {
            id: vehicleId,
            dateAdded: new Date(),
            title: 'Vehicle ' + vehicleId,
            price: 0,
            image: '/placeholder-car.jpg'
          };

          cartItems.value.push(fallbackItem);
          saveClientState();
          openCart();
        }
      }
    } catch (err) {
      console.error('Error adding item to cart:', err);
      error.value = 'Failed to add item to cart.';
    } finally {
      isLoading.value = false;
    }
  }

  const removeFromCart = (vehicleId: string) => {
    cartItems.value = cartItems.value.filter(item => item.id !== vehicleId)
    // Save the updated cart state
    saveClientState();
    // If cart becomes empty, close it
    if (cartItems.value.length === 0) {
       closeCart();
    }
  }

  const clearCart = () => {
    cartItems.value = []
    // Save the updated cart state
    saveClientState();
    // Close the cart when cleared
    closeCart();
  }

  const loadClientState = () => {
    if (process.client) {
      try {
        const storedValue = localStorage.getItem('cart'); // Use store key 'cart'
        if (storedValue) {
          const parsedState = JSON.parse(storedValue);
          // Check for the cartItems property
          if (parsedState && Array.isArray(parsedState)) {
             cartItems.value = parsedState;
          } else {
             cartItems.value = [];
          }
        } else {
           cartItems.value = [];
        }
      } catch (e) {
        console.error('Error loading cart state from localStorage');
        cartItems.value = [];
      }
      // Do not load isCartOpen state from storage, let it default to false
      isCartOpen.value = false;
    }
  }

  // Save cart state to localStorage
  const saveClientState = () => {
    if (process.client) {
      try {
        localStorage.setItem('cart', JSON.stringify(cartItems.value));
      } catch (e) {
        console.error('Error saving cart state to localStorage');
      }
    }
  }

  // Watch for changes to cart items and save to localStorage
  if (process.client) {
    watch(cartItems, () => {
      saveClientState();
    }, { deep: true });
  }

  // Initialize cart state on store creation (client-side only)
  if (process.client) {
    // Use nextTick to ensure this runs after hydration
    nextTick(() => {
      loadClientState();
    });
  }

  return {
    // State
    cartItems,
    isCartOpen,
    isLoading,
    error,
    // Getters
    cartCount,
    cartTotal,
    isInCart,
    // Actions
    openCart,
    closeCart,
    toggleCart,
    addToCart,
    removeFromCart,
    clearCart,
    loadClientState,
    saveClientState
  }
}, {
  persist: false // Disable automatic persistence, we'll handle it manually
})